import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Field } from '@app/interfaces';
import {
    NjuskaloCity,
    NjuskaloLocationApiService,
    NjuskaloPlace,
    NjuskaloRegion,
} from '@app/services/api/integrations/njuskalo-location-api.service';

@Component({
    selector: 'app-form-input-njuskalo-location',
    templateUrl: './form-input-njuskalo-location.component.html',
    styleUrls: ['./form-input-njuskalo-location.component.scss'],
    standalone: false,
})
export class FormInputNjuskaloLocationComponent implements OnInit {
    @Input() field: Field<string>;
    @Input() control: FormControl;

    // BRIEFD-7251: the region/city/place DOM ids used to be hardcoded
    // ('njuskalo-region' etc.), which collided whenever two instances of this
    // component rendered on the same page — e.g. an account with both
    // Njuškalo and Index.hr active gets this same field (same fieldUuid, since
    // IndexHrIntegrationAdapter reuses Njuškalo's njuskalo_location field
    // type_id) listed in both integrations' advertising subsections, so
    // field.fieldUuid alone is NOT guaranteed unique per rendered instance
    // here. A per-component-instance counter guarantees uniqueness regardless.
    private static nextInstanceId = 0;
    readonly instanceId = FormInputNjuskaloLocationComponent.nextInstanceId++;

    tree: NjuskaloRegion[] = [];
    isLoadingTree = true;

    selectedRegion = '';
    selectedCity = '';
    selectedPlace: string | null = null;

    cities: NjuskaloCity[] = [];
    places: NjuskaloPlace[] = [];

    constructor(
        private njuskaloLocationApi: NjuskaloLocationApiService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.njuskaloLocationApi.getTree().subscribe({
            next: (tree) => {
                this.tree = tree;
                this.isLoadingTree = false;

                if (this.field.value) {
                    this.restoreFromValue();
                }

                this.cdr.detectChanges();
            },
            error: () => {
                this.isLoadingTree = false;
                this.cdr.detectChanges();
            },
        });
    }

    get regions(): string[] {
        return this.tree.map((r) => r.region);
    }

    get showCities(): boolean {
        return this.cities.length > 0;
    }

    get showPlaces(): boolean {
        return this.places.length > 0;
    }

    private regionNode(region: string): NjuskaloRegion | undefined {
        return this.tree.find((r) => r.region === region);
    }

    private parseLocationId(): number | null {
        if (typeof this.field.value === 'object' && this.field.value !== null) {
            const key = Object.keys(this.field.value)[0];
            return parseInt(key?.replace(/^_/, ''), 10) || null;
        }

        if (typeof this.field.value === 'string') {
            return parseInt(this.field.value.replace(/^_/, ''), 10) || null;
        }

        return null;
    }

    private restoreFromValue(): void {
        const id = this.parseLocationId();
        if (!id) return;

        for (const region of this.tree) {
            if (region.places?.some((p) => p.id === id)) {
                // Foreign group — country selected directly.
                this.selectedRegion = region.region;
                this.cities = [];
                this.places = region.places;
                this.selectedPlace = `_${id}`;
                this.updateControlValue();
                return;
            }

            const city = region.cities?.find((c) => c.places.some((p) => p.id === id));
            if (city) {
                this.selectedRegion = region.region;
                this.cities = region.cities ?? [];
                this.selectedCity = city.city;
                this.places = city.places;
                this.selectedPlace = `_${id}`;
                this.updateControlValue();
                return;
            }
        }
    }

    onRegionChange(event: Event): void {
        const region = (event.target as HTMLSelectElement).value;
        this.selectedRegion = region;
        this.selectedCity = '';
        this.selectedPlace = null;
        this.cities = [];
        this.places = [];

        const node = this.regionNode(region);
        if (node?.cities?.length) {
            this.cities = node.cities;
        } else if (node?.places?.length) {
            // Foreign group — countries are selectable directly (no municipality level).
            this.places = node.places;
        }

        this.updateControlValue();
    }

    onCityChange(event: Event): void {
        const city = (event.target as HTMLSelectElement).value;
        this.selectedCity = city;
        this.selectedPlace = null;
        this.places = this.cities.find((c) => c.city === city)?.places ?? [];
        this.updateControlValue();
    }

    onPlaceChange(event: Event): void {
        this.selectedPlace = (event.target as HTMLSelectElement).value || null;
        this.updateControlValue();
    }

    private updateControlValue(): void {
        if (this.selectedPlace) {
            const place = this.places.find((p) => `_${p.id}` === this.selectedPlace);
            if (place) {
                this.control.setValue({ [this.selectedPlace]: place.name });
                this.control.setErrors(null);
            } else {
                this.control.setValue(null);
            }
        } else {
            this.control.setValue(null);

            // If the user picked a region but didn't drill down to a leaf, mark incomplete.
            if (this.selectedRegion) {
                this.control.setErrors({ incompleteLocation: true });
            } else {
                this.control.setErrors(null);
            }
        }
        this.cdr.detectChanges();
    }
}
